import { ArrowRightOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import DatePickerWithDateFns from 'modules/common/components/DatePicker';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getDateRangeBounds } from '../../contexts/PhlebotomistDashboardContext/usePhlebotomistStatsData';
import { DateRangeType } from '../../types/phlebotomist-dashboard';
import styles from '../OperationsDashboard/ops-theme.module.css';

const OPS_PICKER_DROPDOWN = 'ops-picker-dropdown';
const OPS_SELECT_DROPDOWN = 'ops-select-dropdown';

interface IFilterOption {
  id: string;
  name: string;
  regionId?: string;
}

interface IOpsPeriodFilterProps {
  value: DateRangeType;
  customRange?: { start: Date; end: Date };
  onChange: (
    dateRange: DateRangeType,
    customRange?: { start: Date; end: Date }
  ) => void;
  presets?: { label: string; value: DateRangeType }[];
  regions?: IFilterOption[];
  regionId?: string;
  onRegionChange?: (regionId: string | undefined) => void;
  regionLocked?: boolean;
  recruiters?: IFilterOption[];
  recruiterId?: string;
  onRecruiterChange?: (recruiterId: string | undefined) => void;
}

const DEFAULT_PRESETS: { label: string; value: DateRangeType }[] = [
  { label: 'Yesterday', value: 'yesterdayCal' },
  { label: 'Last Week', value: 'lastWeekCal' },
  { label: 'Last Month', value: 'lastMonthCal' },
  { label: 'Last Quarter', value: 'lastQuarterCal' },
  { label: 'Last Year', value: 'pYearCal' },
];

const OpsPeriodFilter: FC<IOpsPeriodFilterProps> = ({
  value,
  customRange,
  onChange,
  presets = DEFAULT_PRESETS,
  regions,
  regionId,
  onRegionChange,
  regionLocked,
  recruiters,
  recruiterId,
  onRecruiterChange,
}) => {
  const isCustomActive = value === 'custom';

  // Resolve the actual date bounds for the selected period
  const resolvedBounds = useMemo(
    () => getDateRangeBounds(value, customRange),
    [value, customRange]
  );

  const [localStart, setLocalStart] = useState<Date | null>(
    customRange?.start ?? resolvedBounds.start ?? null
  );
  const [localEnd, setLocalEnd] = useState<Date | null>(
    customRange?.end ?? resolvedBounds.end ?? null
  );

  // Sync local picker state with the resolved bounds whenever the active
  // preset changes, so the inputs reflect the current period after a preset
  // click. While in custom mode, leave local state alone so user edits are
  // preserved between picks.
  useEffect(() => {
    if (isCustomActive) return;
    setLocalStart(resolvedBounds.start ?? null);
    setLocalEnd(resolvedBounds.end ?? null);
  }, [isCustomActive, resolvedBounds.start, resolvedBounds.end]);

  const handlePresetClick = (preset: DateRangeType) => {
    if (preset === 'custom') {
      const start = localStart ?? resolvedBounds.start ?? new Date();
      const end = localEnd ?? resolvedBounds.end ?? new Date();
      setLocalStart(start);
      setLocalEnd(end);
      onChange('custom', { start, end });
      return;
    }
    onChange(preset);
  };

  const handleApply = () => {
    if (localStart && localEnd) {
      onChange('custom', { start: localStart, end: localEnd });
    }
  };

  // Filter recruiters by selected region
  const filteredRecruiters = useMemo(() => {
    if (!recruiters) return [];
    if (!regionId) return recruiters;
    return recruiters.filter((r) => r.regionId === regionId);
  }, [recruiters, regionId]);

  // Render popup inside .opsTheme so it inherits CSS variables
  const getPopupContainer = useCallback(
    (triggerNode: HTMLElement) =>
      (triggerNode.closest(`.${styles.opsTheme}`) as HTMLElement) ||
      (triggerNode.parentNode as HTMLElement),
    []
  );

  const hasFilterDropdowns = regions && onRegionChange;

  return (
    <div className={styles.periodFilter}>
      <span className={styles.periodLabel}>Period</span>

      {presets.map((preset) => (
        <button
          key={preset.value}
          className={`${styles.periodBtn} ${
            value === preset.value ? styles.periodBtnActive : ''
          }`}
          onClick={() => handlePresetClick(preset.value)}
        >
          {preset.label}
        </button>
      ))}

      <button
        className={`${styles.periodBtn} ${
          isCustomActive ? styles.periodBtnActive : ''
        }`}
        onClick={() => handlePresetClick('custom')}
      >
        Custom
      </button>

      <div className={styles.periodCustomGroup}>
        <span className={styles.periodSep}>or</span>

        <DatePickerWithDateFns
          value={localStart}
          onChange={(date) => setLocalStart(date)}
          className={styles.periodDateInput}
          dropdownClassName={OPS_PICKER_DROPDOWN}
          getPopupContainer={getPopupContainer}
          allowClear={false}
          placeholder="Start date"
        />
      </div>

      <div className={styles.periodCustomGroup}>
        <ArrowRightOutlined className={styles.periodArrow} />

        <DatePickerWithDateFns
          value={localEnd}
          onChange={(date) => setLocalEnd(date)}
          className={styles.periodDateInput}
          dropdownClassName={OPS_PICKER_DROPDOWN}
          getPopupContainer={getPopupContainer}
          allowClear={false}
          placeholder="End date"
        />
      </div>

      <button className={styles.periodApplyBtn} onClick={handleApply}>
        Apply
      </button>

      {hasFilterDropdowns && (
        <div className={styles.filterSelectGroup}>
          <span className={styles.filterDivider} />

          <Select
            value={regionId || 'all'}
            onChange={(val) => onRegionChange(val === 'all' ? undefined : val)}
            disabled={regionLocked}
            className={styles.filterSelect}
            dropdownClassName={OPS_SELECT_DROPDOWN}
            getPopupContainer={getPopupContainer}
            showSearch
            filterOption={(input, option) =>
              (option?.children as unknown as string)
                ?.toLowerCase()
                .includes(input.toLowerCase()) ?? false
            }
          >
            <Select.Option key="all" value="all">
              All Regions
            </Select.Option>
            {regions.map((r) => (
              <Select.Option key={r.id} value={r.id}>
                {r.name}
              </Select.Option>
            ))}
          </Select>

          {onRecruiterChange && (
            <Select
              value={recruiterId || 'all'}
              onChange={(val) =>
                onRecruiterChange(val === 'all' ? undefined : val)
              }
              className={styles.filterSelect}
              dropdownClassName={OPS_SELECT_DROPDOWN}
              getPopupContainer={getPopupContainer}
              showSearch
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  ?.toLowerCase()
                  .includes(input.toLowerCase()) ?? false
              }
            >
              <Select.Option key="all" value="all">
                All Recruiters
              </Select.Option>
              {filteredRecruiters.map((r) => (
                <Select.Option key={r.id} value={r.id}>
                  {r.name}
                </Select.Option>
              ))}
            </Select>
          )}
        </div>
      )}
    </div>
  );
};

export default OpsPeriodFilter;
